/**
 * Helpers de derivação para o Catálogo WRICEF — labels textuais e status
 * de revisão que a UI consome diretamente. Mantemos a lógica fora do
 * componente pra facilitar testes e reuso.
 *
 * Tradução por `i18n.t` direto, e não por hook: estas funções não são
 * componentes, e algumas são chamadas de dentro de `map`/`sort` onde não há
 * onde pendurar um `useTranslation`. É o mesmo caminho do `datetime.ts`.
 *
 * A chave é o próprio texto em inglês (`keySeparator: false` no `i18n`), então
 * um idioma sem entrada cai no inglês em vez de mostrar uma chave crua.
 */

import i18n from "@/i18n";
import {
  CONVERSION_LABELS,
  UNKNOWN_STREAM,
  type CleanCoreLevel,
  type WricefCatalogItem,
} from "@/types/wricef";

// ---------------------------------------------------------------------------
// Criticidade — score numérico → label textual
// ---------------------------------------------------------------------------

export type CriticalityLevel = "alta" | "media" | "baixa" | "indefinida";

export interface CriticalityLabel {
  level: CriticalityLevel;
  label: string;
  description: string;
  tone: "high" | "medium" | "low" | "muted";
}

export function getCriticalityLabel(score: number | null): CriticalityLabel {
  if (score == null) {
    return {
      level: "indefinida",
      label: i18n.t("No score"),
      description: i18n.t("Score not yet computed for this object."),
      tone: "muted",
    };
  }
  if (score >= 0.7) {
    return {
      level: "alta",
      label: i18n.t("High"),
      description: i18n.t("High potential impact — prioritize functional review."),
      tone: "high",
    };
  }
  if (score >= 0.4) {
    return {
      level: "media",
      label: i18n.t("Medium"),
      description: i18n.t("Moderate impact — review when possible."),
      tone: "medium",
    };
  }
  return {
    level: "baixa",
    label: i18n.t("Low"),
    description: i18n.t("Low impact — natural candidate to retire or defer."),
    tone: "low",
  };
}

export const CRITICALITY_BAR_COLOR: Record<CriticalityLabel["tone"], string> = {
  high: "bg-red-500",
  medium: "bg-amber-500",
  low: "bg-emerald-500",
  muted: "bg-muted-foreground/40",
};

export const CRITICALITY_BADGE_COLOR: Record<CriticalityLabel["tone"], string> = {
  high: "bg-red-50 text-red-800 border-red-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-emerald-50 text-emerald-800 border-emerald-200",
  muted: "bg-muted text-muted-foreground border-muted",
};

// ---------------------------------------------------------------------------
// Clean core level — grau oficial A/B/C/D (matriz C7/C8, migration 0024)
// ---------------------------------------------------------------------------

export interface CleanCoreLevelInfo {
  level: CleanCoreLevel | null;
  label: string;
  description: string;
  tone: "ok" | "warn" | "danger" | "muted";
}

export function cleanCoreLevelMeta(
  level: CleanCoreLevel | null | undefined,
): CleanCoreLevelInfo {
  switch (level) {
    case "A":
      return {
        level: "A",
        label: "A",
        description:
          i18n.t("Released SAP object — has a release contract. Cloud-ready."),
        tone: "ok",
      };
    case "B":
      return {
        level: "B",
        label: "B",
        description:
          i18n.t("Classic API — SAP names it as such. Works, but not the cloud target."),
        tone: "warn",
      };
    case "C":
      return {
        level: "C",
        label: "C",
        description:
          i18n.t("Internal SAP object with no release contract — not in the Cloudification Repository, or listed as not released."),
        tone: "warn",
      };
    case "D":
      return {
        level: "D",
        label: "D",
        description:
          i18n.t("No API — SAP marks the object as such, or the technique is a modification. Requires redesign."),
        tone: "danger",
      };
    default:
      return {
        level: null,
        label: "—",
        description: i18n.t("No clean-core level assigned."),
        tone: "muted",
      };
  }
}

export const CLEAN_CORE_BADGE_COLOR: Record<CleanCoreLevelInfo["tone"], string> = {
  ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
  warn: "bg-amber-50 text-amber-800 border-amber-200",
  danger: "bg-red-50 text-red-800 border-red-200",
  muted: "bg-muted text-muted-foreground border-muted",
};

// ---------------------------------------------------------------------------
// Status de revisão — derivado de description_pt + wricef_fallback
// ---------------------------------------------------------------------------

export type ReviewStatus = "pronto" | "requer_revisao" | "descricao_pendente";

export interface ReviewStatusInfo {
  status: ReviewStatus;
  label: string;
  description: string;
  tone: "ok" | "warn" | "info";
}

// Marcador casado contra o conteúdo de description_pt gerado pelo backend —
// NÃO traduzir sem alinhar o backend (é um sentinel de contrato, não um label).
const NEEDS_REVIEW_MARKER = "Requer revisão manual";

export function getReviewStatus(item: WricefCatalogItem): ReviewStatusInfo {
  // Classificação caiu em fallback (tipo TADIR não mapeado) — UI sempre
  // sinaliza pro consultor checar mesmo que a descrição esteja OK.
  if (item.wricef_fallback) {
    return {
      status: "requer_revisao",
      label: i18n.t("Needs review"),
      description:
        i18n.t("TADIR type not in the canonical map — WRICEF category defaulted to (E). Confirm manually."),
      tone: "warn",
    };
  }
  // LLM declarou que não conseguiu descrever, ou erro de chamada.
  if (item.description_pt && item.description_pt.includes(NEEDS_REVIEW_MARKER)) {
    return {
      status: "requer_revisao",
      label: i18n.t("Needs review"),
      description:
        i18n.t("Functional description not generated automatically (sparse code or LLM error). Human review required."),
      tone: "warn",
    };
  }
  // Sem descrição mas sem flag → o modelo por objeto não rodou. A passada leve,
  // que é o padrão do painel, nunca o chama.
  if (!item.description_pt) {
    return {
      status: "descricao_pendente",
      label: i18n.t("Description pending"),
      description:
        i18n.t("No functional description. The light pass never writes one; a deep pass does, when the ABAP source was persisted."),
      tone: "info",
    };
  }
  return {
    status: "pronto",
    label: i18n.t("Ready for review"),
    description: i18n.t("Classification + functional description available for review."),
    tone: "ok",
  };
}

export const REVIEW_STATUS_COLORS: Record<ReviewStatusInfo["tone"], string> = {
  ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
  warn: "bg-amber-50 text-amber-800 border-amber-200",
  info: "bg-sky-50 text-sky-800 border-sky-200",
};

// ---------------------------------------------------------------------------
// Módulo — o eixo resolvido (#922)
// ---------------------------------------------------------------------------

/**
 * O rótulo do módulo resolvido. A lista devolve `null` e o agregado devolve
 * `_UNKNOWN` para o mesmo caso, e a tela diz os dois com a frase do card do
 * Diagnóstico.
 *
 * `labels` é o `stream_labels` que a API manda ao lado do agregado (#906): o
 * texto do componente de aplicação no idioma de quem lê, vindo da `TAPLT` do
 * SAP. Quem passa **código** passa o mapa junto; quem já tem o texto pronto
 * (`resolved_stream` da lista, que a API resolve no SQL) chama sem ele.
 *
 * Código fora do mapa vira o próprio código: existe `appl` fora das 24 letras
 * da TAPLT, e balde com contagem e sem nome é pior que balde nomeado pela
 * letra.
 */
export function streamLabel(
  stream: string | null | undefined,
  labels?: Record<string, string>,
): string {
  if (!stream || stream === UNKNOWN_STREAM) return i18n.t("No module identified");
  return labels?.[stream] ?? stream;
}

// ---------------------------------------------------------------------------
// Origem — label legível para zip | tadir | both
// ---------------------------------------------------------------------------

export function getSourceLabel(source: string | null | undefined): string {
  if (!source) return i18n.t("Not informed");
  if (source === "zip") return "SYCM / ATC";
  if (source === "tadir") return i18n.t("TADIR (RFC)");
  if (source === "both") return i18n.t("Both (SYCM + TADIR)");
  return source;
}

// ---------------------------------------------------------------------------
// Explicação textual de cada fator de criticidade
// ---------------------------------------------------------------------------

export interface FactorExplanation {
  key: "uso" | "complexity" | "io_density" | "modulo" | "wricef_weight";
  label: string;
  weight: number;
  describe: (value: number) => string;
}

export const CRITICALITY_FACTOR_EXPLANATIONS: FactorExplanation[] = [
  {
    key: "uso",
    label: i18n.t("Real production usage"),
    weight: 0.3,
    describe: (v) =>
      v >= 0.9
        ? i18n.t("Object called thousands of times — high operational dependency.")
        : v >= 0.5
        ? i18n.t("Significant usage recorded over the last 24 months.")
        : v === 0
        ? i18n.t("No usage records — candidate to retire.")
        : v >= 0.3
        ? i18n.t("Sporadic usage over the last months.")
        : i18n.t("No usage data (SYCM without UPL or pure TADIR) — neutral weight applied."),
  },
  {
    key: "complexity",
    label: i18n.t("Cognitive complexity"),
    weight: 0.25,
    describe: (v) =>
      v >= 0.7
        ? i18n.t("Code with many nesting levels, decisions and flow control.")
        : v >= 0.3
        ? i18n.t("Moderate complexity — readable but needs attention when changed.")
        : i18n.t("Straightforward code — low regression risk on refactor."),
  },
  {
    key: "io_density",
    label: i18n.t("Data I/O density"),
    weight: 0.2,
    describe: (v) =>
      v >= 0.7
        ? i18n.t("Many SELECT/UPDATE/RFC operations per line — touches critical business data.")
        : v >= 0.3
        ? i18n.t("A reasonable mix of read/write.")
        : i18n.t("Little database or RFC interaction — a more computational flow."),
  },
  {
    key: "modulo",
    label: i18n.t("SAP module weight"),
    weight: 0.15,
    describe: (v) =>
      v >= 0.9
        ? i18n.t("Financial/regulatory module — higher risk of accounting impact.")
        : v >= 0.7
        ? i18n.t("Core operations module (SD/MM/PP) — affects the production chain.")
        : v >= 0.5
        ? i18n.t("Intermediate or unknown module — neutral weight.")
        : i18n.t("Support/infra module — contained impact."),
  },
  {
    key: "wricef_weight",
    label: i18n.t("WRICEF type"),
    weight: 0.1,
    describe: (v) =>
      v >= 0.9
        ? i18n.t("Interface — integration points are fragile and critical by nature.")
        : v >= 0.7
        ? i18n.t("Workflow or Conversion — touches approval processes or bulk data.")
        : v >= 0.4
        ? i18n.t("Report or Enhancement — impact contained in functional logic.")
        : i18n.t("Form — low risk (layout/output)."),
  },
];

// ---------------------------------------------------------------------------
// Diagnóstico contextual: conversão, esforço e confiança
// ---------------------------------------------------------------------------

/** True quando a análise contextual rodou (objeto com source). Linhas sem
 * análise (sem source / pipeline legado) caem no caminho estático. */
export function hasRichAnalysis(item: WricefCatalogItem): boolean {
  return item.conversion_tipo != null || item.analysis_confidence != null;
}

/** Label legível pro tipo de conversão; cai no próprio valor se desconhecido. */
export function getConversionLabel(tipo: string | null | undefined): string {
  if (!tipo) return i18n.t("Not classified");
  const label = CONVERSION_LABELS[tipo];
  // O mapa mora em `types/` e é dado, não componente: a tradução acontece aqui,
  // na leitura, e o valor cru do banco continua saindo cru quando não há mapa.
  return label ? i18n.t(label) : tipo;
}

/** Formata esforço em horas → "200h (~25 days)". Assume jornada de 8h.
 * Retorna null quando não há estimativa (não mostrar nada na UI). */
export function formatEffort(hours: number | null | undefined): string | null {
  if (hours == null || hours <= 0) return null;
  const days = Math.round((hours / 8) * 10) / 10;
  const daysFmt = Number.isInteger(days) ? String(days) : days.toFixed(1);
  return `${hours}h (~${daysFmt} ${days === 1 ? i18n.t("day") : i18n.t("days")})`;
}

/**
 * As horas de uma decisão do arquiteto (#1343), com o zero à vista.
 *
 * `formatEffort` devolve `null` em 0 hora (pensado para "sem estimativa"),
 * mas na decisão 0 é um valor de verdade: fora do escopo e standard zeram de
 * propósito, e isso tem de aparecer como "0h", não como "—". A seção do
 * detalhe e a grade do WRICEF Catalog (#1500) usam esta mesma regra.
 */
export function hoursLabel(hours: number | null | undefined): string | null {
  if (hours == null) return null;
  if (hours === 0) return "0h";
  return formatEffort(hours);
}

export type ConfidenceTone = "high" | "medium" | "low" | "muted";

export interface ConfidenceLabel {
  label: string;
  pct: string;
  tone: ConfidenceTone;
}

/** Confiança 0–1 da análise → label + percentual + tom. */
export function getConfidenceLabel(conf: number | null | undefined): ConfidenceLabel {
  if (conf == null) {
    return { label: "—", pct: "—", tone: "muted" };
  }
  const pct = `${Math.round(conf * 100)}%`;
  if (conf >= 0.8) return { label: i18n.t("High"), pct, tone: "high" };
  if (conf >= 0.5) return { label: i18n.t("Medium"), pct, tone: "medium" };
  return { label: i18n.t("Low"), pct, tone: "low" };
}

export const CONFIDENCE_BADGE_COLOR: Record<ConfidenceTone, string> = {
  high: "bg-emerald-50 text-emerald-800 border-emerald-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-red-50 text-red-800 border-red-200",
  muted: "bg-muted text-muted-foreground border-muted",
};
